import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Amazon.css';

const TABS = [
  { key: 'inventory', label: 'Inventory' },
  { key: 'mf-orders', label: 'MF Orders' },
  { key: 'inbound', label: 'Create Shipment' },
  { key: 'shipments', label: 'Shipments' },
  { key: 'settlements', label: 'Settlements' },
  { key: 'planning', label: 'Replenishment' },
  { key: 'sync', label: 'Sync Status' },
];

function AmazonDashboard() {
  const [activeTab, setActiveTab] = useState('inventory');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/amazon/status')
      .then(res => setStatus(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="amazon-page">
        <div className="amazon-loading"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="amazon-page">
      <div className="amazon-page-header">
        <h1>Amazon FBA</h1>
        {status && !status.credentialsConfigured && (
          <span style={{ fontSize: '0.8rem', color: '#C0392B' }}>
            Missing credentials: {status.missingCredentials?.join(', ')}
          </span>
        )}
      </div>

      <div className="amazon-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`amazon-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'inventory' && <InventoryPanel />}
      {activeTab === 'mf-orders' && <MFOrdersPanel />}
      {activeTab === 'inbound' && <InboundCreationPanel />}
      {activeTab === 'shipments' && <ShipmentsPanel />}
      {activeTab === 'settlements' && <SettlementsPanel />}
      {activeTab === 'planning' && <PlanningPanel />}
      {activeTab === 'sync' && <SyncPanel status={status} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inventory Tab (with favorites + inline edit)
// ---------------------------------------------------------------------------

function InventoryPanel() {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('bsr');
  const [order, setOrder] = useState('asc');
  const [editingSku, setEditingSku] = useState(null);
  const [editValues, setEditValues] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const params = { search, sort, order, limit: 500 };
      if (statusFilter === 'active') params.active = 'true';
      const [invRes, sumRes] = await Promise.all([
        axios.get('/api/amazon/inventory', { params }),
        axios.get('/api/amazon/inventory/summary'),
      ]);
      const fetched = invRes.data.items || [];
      setAllItems(fetched);
      setSummary(sumRes.data);
    } catch (err) {
      console.error('Inventory fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [search, sort, order, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Client-side status filter (except favorites which is server-side)
  useEffect(() => {
    if (!statusFilter || statusFilter === 'active') {
      setItems(allItems);
      return;
    }
    const filtered = allItems.filter(item => {
      const qty = item.fulfillable_qty || 0;
      const dos = item.days_of_supply;
      switch (statusFilter) {
        case 'in_stock': return qty > 0;
        case 'out_of_stock': return qty === 0;
        case 'low_stock': return qty > 0 && dos != null && dos < 14;
        case 'has_inbound': return (item.inbound_shipped_qty || 0) + (item.inbound_receiving_qty || 0) > 0;
        case 'unfulfillable': return (item.unfulfillable_qty || 0) > 0;
        default: return true;
      }
    });
    setItems(filtered);
  }, [statusFilter, allItems]);

  const handleSort = (col) => {
    if (sort === col) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(col);
      setOrder('asc');
    }
  };

  const sortArrow = (col) => sort === col ? (order === 'asc' ? ' \u2191' : ' \u2193') : '';

  const toggleActive = async (sellerSku, currentActive) => {
    try {
      await axios.patch(`/api/amazon/inventory/${encodeURIComponent(sellerSku)}/active`, {
        is_active: !currentActive,
      });
      setAllItems(prev => prev.map(item =>
        item.seller_sku === sellerSku ? { ...item, is_active: !currentActive } : item
      ));
    } catch (err) {
      console.error('Active toggle failed:', err);
    }
  };

  const startEdit = (item) => {
    setEditingSku(item.seller_sku);
    setEditValues({
      custom_reorder_point: item.custom_reorder_point || '',
      custom_reorder_qty: item.custom_reorder_qty || '',
    });
  };

  const saveEdit = async (sellerSku) => {
    try {
      await axios.patch(`/api/amazon/sku-config/${encodeURIComponent(sellerSku)}`, {
        custom_reorder_point: editValues.custom_reorder_point ? parseInt(editValues.custom_reorder_point, 10) : null,
        custom_reorder_qty: editValues.custom_reorder_qty ? parseInt(editValues.custom_reorder_qty, 10) : null,
      });
      setEditingSku(null);
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return <div className="amazon-loading"><div className="spinner" /></div>;
  }

  return (
    <>
      {summary && (
        <div className="amazon-summary">
          <div className="amazon-summary-card">
            <div className="label">Total SKUs</div>
            <div className="value">{summary.totalSkus}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Fulfillable</div>
            <div className="value success">{(summary.totalFulfillable || 0).toLocaleString()}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Reserved</div>
            <div className="value">{(summary.totalReserved || 0).toLocaleString()}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Inbound</div>
            <div className="value">{(summary.totalInbound || 0).toLocaleString()}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Low Stock</div>
            <div className="value warning">{summary.lowStockCount}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Out of Stock</div>
            <div className="value danger">{summary.outOfStockCount}</div>
          </div>
        </div>
      )}

      <div className="amazon-search">
        <input
          type="text"
          placeholder="Search by SKU, ASIN, or product name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #ddd', background: 'white', minWidth: '150px' }}
        >
          <option value="">All Status</option>
          <option value="active">Active Only</option>
          <option value="in_stock">In Stock</option>
          <option value="out_of_stock">Out of Stock</option>
          <option value="low_stock">Low Stock (&lt;14 DOS)</option>
          <option value="has_inbound">Has Inbound</option>
          <option value="unfulfillable">Unfulfillable</option>
        </select>
        <span style={{ fontSize: '0.8rem', color: '#888', alignSelf: 'center' }}>
          {items.length} items
        </span>
      </div>

      <div className="amazon-table-wrap">
        {items.length === 0 ? (
          <div className="amazon-empty">{statusFilter ? 'No items match this filter.' : 'No inventory data yet. Run an inventory sync to populate.'}</div>
        ) : (
          <table className="amazon-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>Active</th>
                <th onClick={() => handleSort('seller_sku')}>SKU{sortArrow('seller_sku')}</th>
                <th onClick={() => handleSort('product_name')}>Product{sortArrow('product_name')}</th>
                <th onClick={() => handleSort('asin')}>ASIN{sortArrow('asin')}</th>
                <th className="num" onClick={() => handleSort('fulfillable_qty')}>In Stock{sortArrow('fulfillable_qty')}</th>
                <th className="num" onClick={() => handleSort('reserved_qty')}>Reserved{sortArrow('reserved_qty')}</th>
                <th className="num" onClick={() => handleSort('inbound_shipped_qty')}>Inbound{sortArrow('inbound_shipped_qty')}</th>
                <th className="num" onClick={() => handleSort('total_qty')}>Total{sortArrow('total_qty')}</th>
                <th className="num" onClick={() => handleSort('bsr')}>BSR{sortArrow('bsr')}</th>
                <th className="num" onClick={() => handleSort('velocity_30d')}>Sales 30d{sortArrow('velocity_30d')}</th>
                <th className="num" onClick={() => handleSort('days_of_supply')}>DOS{sortArrow('days_of_supply')}</th>
                <th className="num">Reorder Pt</th>
                <th className="num">Reorder Qty</th>
                <th onClick={() => handleSort('last_synced_at')}>Last Synced{sortArrow('last_synced_at')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.seller_sku}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!item.is_active}
                      onChange={() => toggleActive(item.seller_sku, item.is_active)}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </td>
                  <td className="sku">{item.seller_sku}</td>
                  <td>{item.product_name || '-'}</td>
                  <td>{item.asin || '-'}</td>
                  <td className="num">{item.fulfillable_qty ?? 0}</td>
                  <td className="num">{item.reserved_qty ?? 0}</td>
                  <td className="num">{(item.inbound_shipped_qty ?? 0) + (item.inbound_receiving_qty ?? 0)}</td>
                  <td className="num">{item.total_qty ?? 0}</td>
                  <td className="num">{item.bsr != null ? item.bsr.toLocaleString() : '-'}</td>
                  <td className="num">{item.velocity_30d != null ? item.velocity_30d.toFixed(1) : '-'}</td>
                  <td className="num">
                    <span className={`amazon-badge ${
                      (item.days_of_supply || 0) === 0 ? 'critical' :
                      (item.days_of_supply || 0) < 14 ? 'warning' : 'ok'
                    }`}>
                      {item.days_of_supply ?? '-'}
                    </span>
                  </td>
                  <td className="num" onClick={() => !editingSku && startEdit(item)}>
                    {editingSku === item.seller_sku ? (
                      <input
                        className="amazon-inline-edit"
                        type="number"
                        value={editValues.custom_reorder_point}
                        onChange={e => setEditValues(v => ({ ...v, custom_reorder_point: e.target.value }))}
                        onBlur={() => saveEdit(item.seller_sku)}
                        onKeyDown={e => e.key === 'Enter' && saveEdit(item.seller_sku)}
                        autoFocus
                      />
                    ) : (
                      <span style={{ cursor: 'pointer', color: item.custom_reorder_point ? '#333' : '#ccc' }}>
                        {item.custom_reorder_point ?? '--'}
                      </span>
                    )}
                  </td>
                  <td className="num" onClick={() => !editingSku && startEdit(item)}>
                    {editingSku === item.seller_sku ? (
                      <input
                        className="amazon-inline-edit"
                        type="number"
                        value={editValues.custom_reorder_qty}
                        onChange={e => setEditValues(v => ({ ...v, custom_reorder_qty: e.target.value }))}
                        onBlur={() => saveEdit(item.seller_sku)}
                        onKeyDown={e => e.key === 'Enter' && saveEdit(item.seller_sku)}
                      />
                    ) : (
                      <span style={{ cursor: 'pointer', color: item.custom_reorder_qty ? '#333' : '#ccc' }}>
                        {item.custom_reorder_qty ?? '--'}
                      </span>
                    )}
                  </td>
                  <td className="date">{item.last_synced_at ? new Date(item.last_synced_at).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Create Shipment Tab (Inbound Plans)
// ---------------------------------------------------------------------------

function InboundCreationPanel() {
  const [source, setSource] = useState('favorites');
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);

  // Fetch inventory based on source
  useEffect(() => {
    setLoading(true);
    const params = { limit: 500 };
    if (source === 'favorites') params.favorites = 'true';
    axios.get('/api/amazon/inventory', { params })
      .then(res => setInventoryItems(res.data.items || []))
      .catch(err => console.error('Fetch failed:', err))
      .finally(() => setLoading(false));
  }, [source]);

  // Fetch existing plans
  useEffect(() => {
    axios.get('/api/amazon/inbound/plans')
      .then(res => setPlans(res.data.plans || []))
      .catch(() => {});
  }, []);

  const toggleItem = (sku) => {
    setSelectedItems(prev => {
      const copy = { ...prev };
      if (copy[sku]) {
        delete copy[sku];
      } else {
        const item = inventoryItems.find(i => i.seller_sku === sku);
        copy[sku] = { sellerSku: sku, asin: item?.asin, fnsku: item?.fnsku, quantity: 24 };
      }
      return copy;
    });
  };

  const updateQty = (sku, qty) => {
    setSelectedItems(prev => ({
      ...prev,
      [sku]: { ...prev[sku], quantity: parseInt(qty, 10) || 0 },
    }));
  };

  const selectAll = () => {
    const all = {};
    inventoryItems.forEach(item => {
      all[item.seller_sku] = {
        sellerSku: item.seller_sku,
        asin: item.asin,
        fnsku: item.fnsku,
        quantity: 24,
      };
    });
    setSelectedItems(all);
  };

  const clearAll = () => setSelectedItems({});

  const selectedList = Object.values(selectedItems);
  const totalUnits = selectedList.reduce((s, i) => s + (i.quantity || 0), 0);

  const handleCreate = async () => {
    if (selectedList.length === 0) return;
    if (!window.confirm(`Create inbound plan with ${selectedList.length} SKUs (${totalUnits} units)?`)) return;

    setCreating(true);
    try {
      const res = await axios.post('/api/amazon/inbound/create', { items: selectedList });
      setActivePlan(res.data.plan);
      setSelectedItems({});
      // Refresh plans list
      const plansRes = await axios.get('/api/amazon/inbound/plans');
      setPlans(plansRes.data.plans || []);
    } catch (err) {
      alert('Failed to create inbound plan: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleCreateTO = async (planId) => {
    try {
      await axios.post(`/api/amazon/inbound/plans/${planId}/create-transfer-order`);
      const plansRes = await axios.get('/api/amazon/inbound/plans');
      setPlans(plansRes.data.plans || []);
      alert('Transfer Order created successfully');
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCreateWO = async (planId) => {
    try {
      const res = await axios.post(`/api/amazon/inbound/plans/${planId}/create-work-orders`);
      const plansRes = await axios.get('/api/amazon/inbound/plans');
      setPlans(plansRes.data.plans || []);
      const wos = res.data.workOrders || [];
      alert(`Created ${wos.filter(w => w.id).length} Work Orders`);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleResume = async (planId) => {
    try {
      await axios.post(`/api/amazon/inbound/plans/${planId}/resume`);
      const plansRes = await axios.get('/api/amazon/inbound/plans');
      setPlans(plansRes.data.plans || []);
    } catch (err) {
      alert('Resume failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const statusSteps = ['draft', 'plan_created', 'packing_confirmed', 'placement_confirmed', 'shipped'];

  return (
    <>
      {/* Item Selection */}
      <div className="amazon-inbound-section">
        <h3>Select Items to Ship</h3>
        <div className="amazon-search" style={{ marginBottom: '0.75rem' }}>
          <select
            value={source}
            onChange={e => setSource(e.target.value)}
            style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #ddd', background: 'white' }}
          >
            <option value="active">Active Only</option>
            <option value="all">All Inventory</option>
          </select>
          <button className="amazon-btn secondary sm" onClick={selectAll}>Select All</button>
          <button className="amazon-btn secondary sm" onClick={clearAll}>Clear</button>
          <span style={{ fontSize: '0.8rem', color: '#888', alignSelf: 'center' }}>
            {selectedList.length} selected / {totalUnits} units
          </span>
        </div>

        {loading ? (
          <div className="amazon-loading"><div className="spinner" /></div>
        ) : (
          <div className="amazon-table-wrap" style={{ maxHeight: '400px', overflow: 'auto' }}>
            <table className="amazon-table">
              <thead>
                <tr>
                  <th style={{ width: '36px' }}></th>
                  <th>SKU</th>
                  <th>Product</th>
                  <th className="num">FBA Stock</th>
                  <th className="num">DOS</th>
                  <th className="num">Qty to Send</th>
                </tr>
              </thead>
              <tbody>
                {inventoryItems.map(item => (
                  <tr key={item.seller_sku} className={selectedItems[item.seller_sku] ? 'selected-row' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!selectedItems[item.seller_sku]}
                        onChange={() => toggleItem(item.seller_sku)}
                      />
                    </td>
                    <td className="sku">{item.seller_sku}</td>
                    <td>{item.product_name || '-'}</td>
                    <td className="num">{item.fulfillable_qty ?? 0}</td>
                    <td className="num">
                      <span className={`amazon-badge ${
                        (item.days_of_supply || 0) === 0 ? 'critical' :
                        (item.days_of_supply || 0) < 14 ? 'warning' : 'ok'
                      }`}>
                        {item.days_of_supply ?? '-'}
                      </span>
                    </td>
                    <td className="num">
                      {selectedItems[item.seller_sku] ? (
                        <input
                          className="amazon-inline-edit"
                          type="number"
                          min="1"
                          value={selectedItems[item.seller_sku].quantity}
                          onChange={e => updateQty(item.seller_sku, e.target.value)}
                          style={{ width: '60px' }}
                        />
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Review & Submit */}
      {selectedList.length > 0 && (
        <div className="amazon-inbound-section" style={{ marginTop: '1rem' }}>
          <div className="amazon-inbound-review">
            <div className="review-stat">
              <div className="label">SKUs</div>
              <div className="value">{selectedList.length}</div>
            </div>
            <div className="review-stat">
              <div className="label">Total Units</div>
              <div className="value">{totalUnits}</div>
            </div>
            <div className="review-stat">
              <div className="label">Ship From</div>
              <div className="value" style={{ fontSize: '0.8rem' }}>Rewined - 4500 Goer Dr</div>
            </div>
            <button
              className="amazon-btn primary"
              onClick={handleCreate}
              disabled={creating || selectedList.length === 0}
              style={{ alignSelf: 'center' }}
            >
              {creating ? 'Creating...' : 'Create Inbound Plan'}
            </button>
          </div>
        </div>
      )}

      {/* Existing Plans */}
      {plans.length > 0 && (
        <div className="amazon-inbound-section" style={{ marginTop: '1.5rem' }}>
          <h3>Inbound Plans</h3>
          <div className="amazon-table-wrap">
            <table className="amazon-table">
              <thead>
                <tr>
                  <th>Plan ID</th>
                  <th>Status</th>
                  <th className="num">Items</th>
                  <th>Shipments</th>
                  <th>Transfer Order</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map(plan => {
                  const stepIdx = statusSteps.indexOf(plan.status);
                  return (
                    <tr key={plan.id}>
                      <td className="sku">{plan.inbound_plan_id || plan.id?.slice(0, 8)}</td>
                      <td>
                        <div className="amazon-step-indicator">
                          {statusSteps.map((step, i) => (
                            <span
                              key={step}
                              className={`step ${i <= stepIdx ? 'done' : ''} ${plan.status === 'error' && i === stepIdx ? 'error' : ''}`}
                              title={step.replace(/_/g, ' ')}
                            />
                          ))}
                        </div>
                        <span className={`amazon-badge ${plan.status === 'error' ? 'critical' : plan.status === 'shipped' ? 'ok' : 'pending'}`}>
                          {plan.status}
                        </span>
                      </td>
                      <td className="num">{plan.amazon_inbound_plan_items?.length || 0}</td>
                      <td>{plan.shipment_ids ? (plan.shipment_ids || []).join(', ') : '-'}</td>
                      <td>
                        {plan.netsuite_transfer_order_tranid || '-'}
                      </td>
                      <td>{plan.created_at ? new Date(plan.created_at).toLocaleDateString() : '-'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          {plan.status === 'error' && (
                            <button className="amazon-btn primary sm" onClick={() => handleResume(plan.inbound_plan_id)}>
                              Resume
                            </button>
                          )}
                          {(plan.status === 'placement_confirmed' || plan.status === 'shipped') && !plan.netsuite_transfer_order_id && (
                            <button className="amazon-btn primary sm" onClick={() => handleCreateTO(plan.inbound_plan_id)}>
                              Create TO
                            </button>
                          )}
                          {plan.netsuite_transfer_order_id && (
                            <button className="amazon-btn secondary sm" onClick={() => handleCreateWO(plan.inbound_plan_id)}>
                              Create WOs
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Shipments Tab
// ---------------------------------------------------------------------------

function ShipmentsPanel() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const params = statusFilter ? { status: statusFilter } : {};
    axios.get('/api/amazon/shipments', { params })
      .then(res => setShipments(res.data.shipments || []))
      .catch(err => console.error('Shipments fetch failed:', err))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  if (loading) {
    return <div className="amazon-loading"><div className="spinner" /></div>;
  }

  return (
    <>
      <div className="amazon-search">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #ddd', background: 'white' }}
        >
          <option value="">All Statuses</option>
          <option value="WORKING">Working</option>
          <option value="SHIPPED">Shipped</option>
          <option value="RECEIVING">Receiving</option>
          <option value="CLOSED">Closed</option>
        </select>
      </div>

      <div className="amazon-table-wrap">
        {shipments.length === 0 ? (
          <div className="amazon-empty">No inbound shipments found.</div>
        ) : (
          <table className="amazon-table">
            <thead>
              <tr>
                <th>Shipment ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Destination</th>
                <th className="num">Items</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {shipments.map(s => (
                <tr key={s.shipment_id}>
                  <td className="sku">{s.shipment_id}</td>
                  <td>{s.shipment_name || '-'}</td>
                  <td>
                    <span className={`amazon-badge ${
                      s.status === 'CLOSED' ? 'ok' :
                      s.status === 'RECEIVING' ? 'approved' :
                      s.status === 'SHIPPED' ? 'approved' : 'pending'
                    }`}>
                      {s.status}
                    </span>
                  </td>
                  <td>{s.destination_fulfillment_center_id || '-'}</td>
                  <td className="num">{s.amazon_inbound_shipment_items?.length || 0}</td>
                  <td>{s.updated_at ? new Date(s.updated_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Settlements Tab
// ---------------------------------------------------------------------------

function SettlementsPanel() {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(null);

  useEffect(() => {
    axios.get('/api/amazon/settlements')
      .then(res => setSettlements(res.data.settlements || []))
      .catch(err => console.error('Settlements fetch failed:', err))
      .finally(() => setLoading(false));
  }, []);

  const handlePost = async (settlementId) => {
    if (!window.confirm(`Post settlement ${settlementId} to NetSuite?`)) return;
    setPosting(settlementId);
    try {
      await axios.post(`/api/amazon/settlements/${settlementId}/post`);
      const res = await axios.get('/api/amazon/settlements');
      setSettlements(res.data.settlements || []);
    } catch (err) {
      alert('Failed to post settlement: ' + (err.response?.data?.error || err.message));
    } finally {
      setPosting(null);
    }
  };

  if (loading) {
    return <div className="amazon-loading"><div className="spinner" /></div>;
  }

  const formatCurrency = (val) => {
    if (val == null) return '-';
    return '$' + Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="amazon-table-wrap">
      {settlements.length === 0 ? (
        <div className="amazon-empty">No settlements found. Run a settlement sync to import.</div>
      ) : (
        <table className="amazon-table">
          <thead>
            <tr>
              <th>Settlement ID</th>
              <th>Period</th>
              <th>Status</th>
              <th className="num">Total Amount</th>
              <th className="num">Deposit</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {settlements.map(s => (
              <tr key={s.settlement_id}>
                <td className="sku">{s.settlement_id}</td>
                <td>
                  {s.settlement_start_date ? new Date(s.settlement_start_date).toLocaleDateString() : '?'}
                  {' - '}
                  {s.settlement_end_date ? new Date(s.settlement_end_date).toLocaleDateString() : '?'}
                </td>
                <td>
                  <span className={`amazon-badge ${s.status || 'pending'}`}>
                    {s.status || 'pending'}
                  </span>
                </td>
                <td className="num">{formatCurrency(s.total_amount)}</td>
                <td className="num">{formatCurrency(s.deposit_amount)}</td>
                <td>
                  {s.status === 'reviewed' && (
                    <button
                      className="amazon-btn primary sm"
                      onClick={() => handlePost(s.settlement_id)}
                      disabled={posting === s.settlement_id}
                    >
                      {posting === s.settlement_id ? 'Posting...' : 'Post to NS'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Planning Tab
// ---------------------------------------------------------------------------

function PlanningPanel() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get('/api/amazon/planning');
      setPlans(res.data.plans || []);
    } catch (err) {
      console.error('Planning fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleEvaluate = async () => {
    setEvaluating(true);
    try {
      await axios.post('/api/amazon/planning/evaluate');
      await fetchPlans();
    } catch (err) {
      alert('Evaluation failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setEvaluating(false);
    }
  };

  const handleApprove = async (planId) => {
    try {
      await axios.post(`/api/amazon/planning/${planId}/approve`);
      await fetchPlans();
    } catch (err) {
      alert('Approval failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDismiss = async (planId) => {
    if (!window.confirm('Dismiss this replenishment plan?')) return;
    try {
      await axios.post(`/api/amazon/planning/${planId}/dismiss`, { reason: 'Dismissed by operator' });
      await fetchPlans();
    } catch (err) {
      alert('Dismiss failed: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return <div className="amazon-loading"><div className="spinner" /></div>;
  }

  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <button
          className="amazon-btn primary"
          onClick={handleEvaluate}
          disabled={evaluating}
        >
          {evaluating ? 'Evaluating...' : 'Run Replenishment Evaluation'}
        </button>
      </div>

      <div className="amazon-table-wrap">
        {plans.length === 0 ? (
          <div className="amazon-empty">No replenishment plans. Run an evaluation to generate recommendations.</div>
        ) : (
          <table className="amazon-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Priority</th>
                <th>Status</th>
                <th className="num">Current DOS</th>
                <th className="num">Recommended Qty</th>
                <th className="num">Batch Qty</th>
                <th>Action Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.id}>
                  <td className="sku">{p.seller_sku}</td>
                  <td>
                    <span className={`amazon-badge ${
                      p.priority === 'critical' ? 'critical' :
                      p.priority === 'high' ? 'warning' : 'ok'
                    }`}>
                      {p.priority}
                    </span>
                  </td>
                  <td>
                    <span className={`amazon-badge ${p.status || 'pending'}`}>
                      {p.status || 'pending'}
                    </span>
                  </td>
                  <td className="num">{p.current_days_of_supply ?? '-'}</td>
                  <td className="num">{p.recommended_qty ?? '-'}</td>
                  <td className="num">{p.batch_adjusted_qty ?? p.recommended_qty ?? '-'}</td>
                  <td>{p.action_type || '-'}</td>
                  <td>
                    {p.status === 'pending' && (
                      <>
                        <button className="amazon-btn primary sm" onClick={() => handleApprove(p.id)} style={{ marginRight: '0.25rem' }}>
                          Approve
                        </button>
                        <button className="amazon-btn secondary sm" onClick={() => handleDismiss(p.id)}>
                          Dismiss
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sync Status Tab (with reconciliation card)
// ---------------------------------------------------------------------------

function SyncPanel({ status }) {
  const [syncing, setSyncing] = useState({});
  const [logs, setLogs] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get('/api/amazon/sync-log', { params: { limit: 20 } }),
      axios.get('/api/amazon/reconciliations').catch(() => ({ data: { reconciliations: [] } })),
    ]).then(([logRes, reconRes]) => {
      setLogs(logRes.data.logs || []);
      setReconciliations(reconRes.data.reconciliations || []);
    }).catch(() => {});
  }, []);

  const triggerSync = async (endpoint, key) => {
    setSyncing(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(endpoint);
      const [logRes, reconRes] = await Promise.all([
        axios.get('/api/amazon/sync-log', { params: { limit: 20 } }),
        axios.get('/api/amazon/reconciliations').catch(() => ({ data: { reconciliations: [] } })),
      ]);
      setLogs(logRes.data.logs || []);
      setReconciliations(reconRes.data.reconciliations || []);
    } catch (err) {
      alert('Sync failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSyncing(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleAdjust = async (reconId) => {
    if (!window.confirm('Create NetSuite inventory adjustment for these variances?')) return;
    try {
      await axios.post(`/api/amazon/reconciliations/${reconId}/adjust`);
      const res = await axios.get('/api/amazon/reconciliations');
      setReconciliations(res.data.reconciliations || []);
    } catch (err) {
      alert('Adjustment failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const syncJobs = [
    { key: 'inventorySync', label: 'Inventory Sync', endpoint: '/api/amazon/sync/inventory', desc: 'Fetch FBA inventory levels from SP-API' },
    { key: 'inboundShipmentSync', label: 'Shipment Sync', endpoint: '/api/amazon/sync/shipments', desc: 'Sync inbound shipment statuses' },
    { key: 'settlementReportIngest', label: 'Settlement Sync', endpoint: '/api/amazon/sync/settlements', desc: 'Download and parse settlement reports' },
    { key: 'inventoryReconciliation', label: 'Inventory Reconciliation', endpoint: '/api/amazon/sync/reconciliation', desc: 'Compare Amazon vs NetSuite FBA inventory' },
  ];

  return (
    <>
      <div className="amazon-sync-panel">
        {syncJobs.map(job => {
          const jobStatus = status?.jobs?.[job.key];
          return (
            <div key={job.key} className="amazon-sync-card">
              <h3>{job.label}</h3>
              <div className="sync-meta">
                <span>{job.desc}</span>
                {jobStatus?.cronExpression && (
                  <span>Schedule: {jobStatus.cronExpression}</span>
                )}
                {jobStatus?.lastRunInDb?.finished_at && (
                  <span>Last run: {new Date(jobStatus.lastRunInDb.finished_at).toLocaleString()}</span>
                )}
                {jobStatus?.lastRunInDb?.status && (
                  <span>Result: {jobStatus.lastRunInDb.status} ({jobStatus.lastRunInDb.records_processed || 0} records)</span>
                )}
              </div>
              <div className="sync-actions">
                <button
                  className="amazon-btn primary sm"
                  onClick={() => triggerSync(job.endpoint, job.key)}
                  disabled={syncing[job.key] || jobStatus?.isRunning}
                >
                  {syncing[job.key] || jobStatus?.isRunning ? 'Running...' : 'Run Now'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reconciliation History */}
      {reconciliations.length > 0 && (
        <>
          <h3 style={{ margin: '1.5rem 0 0.75rem', fontSize: '1rem', color: '#555' }}>Reconciliation History</h3>
          <div className="amazon-table-wrap">
            <table className="amazon-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="num">Compared</th>
                  <th className="num">Matched</th>
                  <th className="num">Variances</th>
                  <th className="num">Variance Units</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reconciliations.map(r => (
                  <tr key={r.id}>
                    <td>{r.reconciliation_date}</td>
                    <td>
                      <span className={`amazon-badge ${
                        r.status === 'matched' ? 'ok' :
                        r.status === 'adjusted' ? 'approved' :
                        r.status === 'variance_found' ? 'warning' : 'pending'
                      }`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="num">{r.total_skus_compared}</td>
                    <td className="num">{r.skus_matched}</td>
                    <td className="num">{r.skus_with_variance}</td>
                    <td className="num">{r.total_variance_units}</td>
                    <td>
                      {r.status === 'variance_found' && (
                        <button className="amazon-btn primary sm" onClick={() => handleAdjust(r.id)}>
                          Adjust in NS
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {logs.length > 0 && (
        <>
          <h3 style={{ margin: '1.5rem 0 0.75rem', fontSize: '1rem', color: '#555' }}>Recent Sync Log</h3>
          <div className="amazon-table-wrap">
            <table className="amazon-table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th className="num">Records</th>
                  <th className="num">Errors</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={log.id || i}>
                    <td>{log.sync_type || log.job_name || '-'}</td>
                    <td>
                      <span className={`amazon-badge ${log.status === 'completed' ? 'ok' : log.status === 'failed' ? 'critical' : 'pending'}`}>
                        {log.status}
                      </span>
                    </td>
                    <td>{log.started_at ? new Date(log.started_at).toLocaleString() : '-'}</td>
                    <td className="num">{log.records_processed ?? '-'}</td>
                    <td className="num">{log.records_errored ?? '-'}</td>
                    <td>
                      {log.started_at && log.finished_at
                        ? `${((new Date(log.finished_at) - new Date(log.started_at)) / 1000).toFixed(1)}s`
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// MF Orders Tab
// ---------------------------------------------------------------------------

function MFOrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingFulfillments, setSyncingFulfillments] = useState(false);
  const [importingId, setImportingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (search) params.search = search;
      if (orderStatus) params.orderStatus = orderStatus;
      if (importStatus) params.importStatus = importStatus;

      const [ordersRes, summaryRes] = await Promise.all([
        axios.get('/api/amazon/mf-orders', { params }),
        axios.get('/api/amazon/mf-orders/summary'),
      ]);
      setOrders(ordersRes.data.orders || []);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('MF orders fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [search, orderStatus, importStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post('/api/amazon/sync/mf-orders');
      await fetchData();
    } catch (err) {
      console.error('MF sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncFulfillments = async () => {
    setSyncingFulfillments(true);
    try {
      await axios.post('/api/amazon/sync/mf-fulfillments');
      await fetchData();
    } catch (err) {
      console.error('MF fulfillment sync failed:', err);
    } finally {
      setSyncingFulfillments(false);
    }
  };

  const handleImport = async (amazonOrderId) => {
    setImportingId(amazonOrderId);
    try {
      await axios.post(`/api/amazon/mf-orders/${encodeURIComponent(amazonOrderId)}/import`);
      await fetchData();
    } catch (err) {
      console.error('MF import failed:', err);
    } finally {
      setImportingId(null);
    }
  };

  const handleRetryFailed = async () => {
    try {
      await axios.post('/api/amazon/mf-orders/retry-failed');
      await fetchData();
    } catch (err) {
      console.error('Retry failed:', err);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const importStatusBadge = (status) => {
    const map = {
      pending: 'pending',
      ready: 'approved',
      importing: 'pending',
      imported: 'posted',
      failed: 'critical',
      skipped: 'pending',
    };
    return map[status] || 'pending';
  };

  const orderStatusBadge = (status) => {
    const map = {
      Unshipped: 'warning',
      PartiallyShipped: 'warning',
      Shipped: 'ok',
      Canceled: 'critical',
    };
    return map[status] || 'pending';
  };

  if (loading) {
    return <div className="amazon-loading"><div className="spinner" /></div>;
  }

  return (
    <>
      {summary && (
        <div className="amazon-summary">
          <div className="amazon-summary-card">
            <div className="label">Total Orders</div>
            <div className="value">{summary.total}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Pending</div>
            <div className="value">{(summary.pending || 0) + (summary.ready || 0)}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Imported</div>
            <div className="value success">{summary.imported}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Failed</div>
            <div className="value danger">{summary.failed}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Fulfilled</div>
            <div className="value">{summary.fulfilled || 0}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">Shipped (AMZ)</div>
            <div className="value">{summary.shippedViaAmazon || 0}</div>
          </div>
          <div className="amazon-summary-card">
            <div className="label">AMZ Confirmed</div>
            <div className="value success">{summary.amazonConfirmed || 0}</div>
          </div>
        </div>
      )}

      <div className="amazon-search">
        <input
          type="text"
          placeholder="Search by order ID, NS SO#, or buyer..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          value={orderStatus}
          onChange={e => setOrderStatus(e.target.value)}
          style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #ddd', background: 'white', minWidth: '140px' }}
        >
          <option value="">All Order Status</option>
          <option value="Unshipped">Unshipped</option>
          <option value="Shipped">Shipped</option>
          <option value="Canceled">Canceled</option>
        </select>
        <select
          value={importStatus}
          onChange={e => setImportStatus(e.target.value)}
          style={{ padding: '0.6rem', borderRadius: '6px', border: '1px solid #ddd', background: 'white', minWidth: '140px' }}
        >
          <option value="">All Import Status</option>
          <option value="pending">Pending</option>
          <option value="ready">Ready</option>
          <option value="imported">Imported</option>
          <option value="failed">Failed</option>
        </select>
        <button className="amazon-btn primary" onClick={handleSync} disabled={syncing}>
          {syncing ? 'Syncing...' : 'Sync MF Orders'}
        </button>
        <button className="amazon-btn secondary" onClick={handleSyncFulfillments} disabled={syncingFulfillments}>
          {syncingFulfillments ? 'Syncing...' : 'Sync Fulfillments'}
        </button>
        {summary && summary.failed > 0 && (
          <button className="amazon-btn secondary" onClick={handleRetryFailed}>
            Retry Failed ({summary.failed})
          </button>
        )}
        <span style={{ fontSize: '0.8rem', color: '#888', alignSelf: 'center' }}>
          {orders.length} orders
        </span>
      </div>

      <div className="amazon-table-wrap">
        {orders.length === 0 ? (
          <div className="amazon-empty">No MF orders found. Click "Sync MF Orders" to fetch from Amazon.</div>
        ) : (
          <table className="amazon-table">
            <thead>
              <tr>
                <th>Amazon Order ID</th>
                <th>Purchase Date</th>
                <th>Buyer</th>
                <th className="num">Items</th>
                <th>Order Status</th>
                <th>Import Status</th>
                <th>NS SO#</th>
                <th>Tracking</th>
                <th>AMZ Status</th>
                <th>Error</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.amazon_order_id}>
                  <td className="sku">{order.amazon_order_id}</td>
                  <td>{formatDate(order.purchase_date)}</td>
                  <td>{order.buyer_name || order.ship_address_name || '-'}</td>
                  <td className="num">{order.number_of_items || (order.line_items?.length) || '-'}</td>
                  <td>
                    <span className={`amazon-badge ${orderStatusBadge(order.order_status)}`}>
                      {order.order_status}
                    </span>
                  </td>
                  <td>
                    <span className={`amazon-badge ${importStatusBadge(order.import_status)}`}>
                      {order.import_status}
                    </span>
                  </td>
                  <td>{order.netsuite_so_tranid || '-'}</td>
                  <td style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {order.fulfillment_status === 'shipped_via_amazon'
                      ? 'Shipped via AMZ'
                      : order.tracking_numbers?.length > 0
                        ? order.tracking_numbers.join(', ')
                        : (order.fulfillment_status === 'pending' && order.import_status === 'imported' ? 'Awaiting' : '-')
                    }
                  </td>
                  <td>
                    {order.amazon_confirmation_status === 'not_required' ? (
                      <span className="amazon-badge" style={{ opacity: 0.5 }}>n/a</span>
                    ) : order.amazon_confirmation_status && order.amazon_confirmation_status !== 'pending' ? (
                      <span className={`amazon-badge ${
                        order.amazon_confirmation_status === 'confirmed' ? 'ok' :
                        order.amazon_confirmation_status === 'failed' ? 'critical' :
                        order.amazon_confirmation_status === 'skipped' ? 'pending' : 'pending'
                      }`}>
                        {order.amazon_confirmation_status}
                      </span>
                    ) : (order.fulfillment_status === 'fulfilled' ? (
                      <span className="amazon-badge warning">pending</span>
                    ) : '-')}
                  </td>
                  <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {order.import_error || ''}
                  </td>
                  <td>
                    {order.import_status !== 'imported' && order.import_status !== 'importing' && (
                      <button
                        className="amazon-btn sm primary"
                        onClick={() => handleImport(order.amazon_order_id)}
                        disabled={importingId === order.amazon_order_id}
                      >
                        {importingId === order.amazon_order_id ? '...' : 'Import'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

export default AmazonDashboard;
